
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import CaseStudyCard from './components/CaseStudyCard';
import CTASection from '../../components/feature/CTASection';

export default function CaseStudiesPage() {
  useEffect(() => {
    document.title = 'Client Success Stories & Case Studies | SparkBoost Labs';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Discover how SparkBoost Labs helped service-based companies achieve 300% revenue growth with AI-powered automation systems. Real results from real companies.');
    }
  }, []);

  const caseStudies = [
    {
      id: 'tech-consult-pro',
      company: 'TechConsult Pro',
      industry: 'Technology Consulting',
      challenge: 'Struggling with inconsistent lead generation and manual outreach processes',
      solution: 'AI-powered outbound automation system with smart lead qualification',
      results: {
        'Qualified Leads': '127 qualified leads in 90 days',
        'Meeting Bookings': '40% increase in meeting bookings',
        'Time Savings': '65% reduction in manual outreach time',
        'Pipeline Value': '$2.3M in new pipeline generated'
      },
      testimonial: {
        quote: 'SparkBoost Labs transformed our entire sales process. We went from feast-or-famine to predictable revenue growth.',
        author: 'Sarah V.',
        position: 'CEO, Technology Consulting Firm'
      },
      image: 'https://static.readdy.ai/image/f3375ab72d7f65b358dc6cf884e758f7/bf982d6551cae17c0870abffd24ce017.jpeg',
      isClientPhoto: true
    },
    {
      id: 'legal-solutions-inc',
      company: 'Legal Solutions Inc',
      industry: 'Legal Services',
      challenge: 'Low conversion rates and lengthy sales cycles',
      solution: 'Sales process optimization with CRM automation and lead nurturing',
      results: {
        'Revenue Growth': '300% revenue increase in 6 months',
        'Close Rate': '65% improvement in close rates',
        'Sales Cycle': '50% shorter sales cycles',
        'Client Retention': '85% client retention rate'
      },
      testimonial: {
        quote: 'The systematic approach to sales optimization delivered results beyond our expectations. Our practice has never beens stronger.',
        author: 'Jennifer W.',
        position: 'Managing Partner, Professional Services Firm'
      },
      image: 'https://static.readdy.ai/image/f3375ab72d7f65b358dc6cf884e758f7/59aa68c36ae93c7787ab2b6082bdb2d8.jpeg',
      isClientPhoto: true
    },
    {
      id: 'growth-partners',
      company: 'Growth Partners',
      industry: 'Business Consulting',
      challenge: 'Limited online presence and difficulty attracting ideal clients',
      solution: 'Authority-building content system with SEO optimization',
      results: {
        'Inbound Leads': '5x increase in inbound leads',
        'Organic Traffic': '180% growth in organic traffic',
        'Lead Quality': '92% improvement in lead quality',
        'New Contracts': '$1.8M in new client contracts'
      },
      testimonial: {
        quote: 'The content strategy positioned us as thought leaders in our industry. High-quality prospects now come to us.',
        author: 'Amir R.',
        position: 'Founder, Digital Marketing Agency'
      },
      image: 'https://static.readdy.ai/image/f3375ab72d7f65b358dc6cf884e758f7/e8356fec4c70d82f38ead84d15aa3ef6.jpeg',
      isClientPhoto: true
    },
    {
      id: 'consulting-dynamics',
      company: 'Consulting Dynamics',
      industry: 'Management Consulting',
      challenge: 'Scaling beyond founder-led sales and operational inefficiencies',
      solution: 'Complete revenue operations overhaul with team training',
      results: {
        'ARR Growth': 'Scaled from $500K to $2M ARR',
        'Revenue Increase': '250% revenue growth',
        'Productivity': '4x team productivity increase',
        'Client Satisfaction': '95% client satisfaction score'
      },
      testimonial: {
        quote: 'SparkBoost Labs helped us build systems that scale. We finally broke through the revenue ceiling.',
        author: 'David T.',
        position: 'CEO, Management Consulting Firm'
      },
      image: 'https://static.readdy.ai/image/f3375ab72d7f65b358dc6cf884e758f7/3f8b2c1a9e7d6f5e4c3b2a1098765432.jpeg',
      isClientPhoto: false
    },
    {
      id: 'digital-marketing-pros',
      company: 'Digital Marketing Pros',
      industry: 'Digital Marketing',
      challenge: 'Inconsistent client acquisition and project delivery bottlenecks',
      solution: 'Integrated automation system with project management optimization',
      results: {
        'Monthly Revenue': '200% increase in monthly recurring revenue',
        'Project Delivery': '75% faster project delivery',
        'Client Retention': '60% reduction in client churn',
        'Annual Revenue': '$3.2M annual revenue milestone'
      },
      testimonial: {
        quote: 'The automation systems freed up our team to focus on strategy while ensuring consistent client results.',
        author: 'Lisa P.',
        position: 'Founder, Digital Marketing Agency'
      },
      image: 'https://static.readdy.ai/image/f3375ab72d7f65b358dc6cf884e758f7/7a8b9c0d1e2f3a4b5c6d7e8f90123456.jpeg',
      isClientPhoto: false
    },
    {
      id: 'financial-advisory-group',
      company: 'Financial Advisory Group',
      industry: 'Financial Services',
      challenge: 'Difficulty building trust with prospects and long sales cycles',
      solution: 'Content marketing strategy with automated nurture sequences',
      results: {
        'Qualified Prospects': '150% increase in qualified prospects',
        'Sales Cycle': '45% shorter sales cycles',
        'Client Value': '80% improvement in client lifetime value',
        'AUM Growth': '$4.1M in assets under management growth'
      },
      testimonial: {
        quote: 'The trust-building content strategy transformed how prospects perceive our expertise. Closing deals became much easier.',
        author: 'Robert K.',
        position: 'Principal, Financial Advisory Firm'
      },
      image: 'https://static.readdy.ai/image/f3375ab72d7f65b358dc6cf884e758f7/9f8e7d6c5b4a3928170615243958472.jpeg',
      isClientPhoto: false
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#1B365D] to-[#2BB6A3] py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-5xl font-bold mb-6">
                Real Results from Real Companies
              </h1>
              <p className="text-xl mb-8 opacity-90">
                Discover how service-based companies achieved predictable revenue growth 
                with our AI-powered systems and proven methodologies.
              </p>
              <div className="flex flex-wrap justify-center gap-8 mb-8">
                <div className="text-center">
                  <div className="text-3xl font-bold">75+</div>
                  <div className="text-sm opacity-80">Success Stories</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">300%</div>
                  <div className="text-sm opacity-80">Average Revenue Growth</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">$50M+</div>
                  <div className="text-sm opacity-80">Client Revenue Generated</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Case Studies Grid */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#1B365D] mb-4">
                Client Success Stories
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                See how we've helped companies across different industries transform their revenue systems 
                and achieve sustainable growth.
              </p>
            </div>
            
            <div className="space-y-20">
              {caseStudies.map((study, index) => (
                <CaseStudyCard 
                  key={study.id} 
                  study={study} 
                  reverse={index % 2 === 1} 
                />
              ))}
            </div>
          </div>
        </section>

        {/* Results Summary */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#1B365D] mb-4">
                Proven Track Record
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our systematic approach delivers consistent results across industries and company sizes.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="w-16 h-16 bg-[#2BB6A3] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-trophy-line text-2xl text-white"></i>
                </div>
                <div className="text-3xl font-bold text-[#1B365D] mb-2">75+</div>
                <p className="text-gray-600">Companies Transformed</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-[#2BB6A3] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-line-chart-line text-2xl text-white"></i>
                </div>
                <div className="text-3xl font-bold text-[#1B365D] mb-2">300%</div>
                <p className="text-gray-600">Average Revenue Increase</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-[#2BB6A3] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <div className="text-3xl font-bold text-[#1B365D] mb-2">90</div>
                <p className="text-gray-600">Days to First Results</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-[#2BB6A3] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
                </div>
                <div className="text-3xl font-bold text-[#1B365D] mb-2">$50M+</div>
                <p className="text-gray-600">Revenue Generated</p>
              </div>
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </div>
  );
}
